import { useAuth } from "@clerk/clerk-react"
import { useQuery } from "@tanstack/react-query"
import { useClerkEndpoint } from "./use-clerk-endpoint"

interface Role {
  id: string
  name: string
  permissions: string[]
}

interface UserPermissionsResponse {
  userId: string
  organizationId: string
  roles: Role[]
  allPermissions: string[]
  ssoEnabled: boolean
}

interface PermissionsResult extends UserPermissionsResponse {
  accessDenied?: boolean
}

export function useUserPermissions() {
  const { isSignedIn, isLoaded } = useAuth()
  const endpoint = useClerkEndpoint()

  const { data, isLoading, error } = useQuery({
    queryKey: ["user-permissions"],
    queryFn: async (): Promise<PermissionsResult> => {
      const response = await endpoint("/users/me/permissions")
      if (response.status === 403) {
        return { userId: "", organizationId: "", roles: [], allPermissions: [], ssoEnabled: false, accessDenied: true }
      }
      if (!response.ok) {
        console.error("Failed to fetch permissions:", response.status, response.statusText)
        return { userId: "", organizationId: "", roles: [], allPermissions: [], ssoEnabled: false }
      }
      return response.json() as Promise<UserPermissionsResponse>
    },
    enabled: isLoaded && isSignedIn, // Wait for Clerk to load AND user to be signed in
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchInterval: 15 * 60 * 1000, // auto-refresh every 15 minutes
    retry: false, // Don't retry on failure
  })

  return {
    permissions: data?.allPermissions || [],
    roles: data?.roles || [],
    hasPermission: (permission: string) => data?.allPermissions?.includes(permission) || false,
    hasRole: (roleId: string) => data?.roles?.some((r) => r.id === roleId) || false,
    ssoEnabled: data?.ssoEnabled || false,
    isLoading,
    error,
    accessDenied: data?.accessDenied || false,
  }
}
